import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { commandForKey, focusableWithin } from './input-commands';

/**
 * Modal dialog: focus moves inside on open, Tab stays inside, the "back" command (Escape / TV Back)
 * cancels, and focus returns to the control that opened it. Clicking outside does nothing, so a
 * stray click never confirms or discards anything.
 */
export function Dialog({
  open,
  title,
  description,
  onCancel,
  children,
  initialFocus = 'first',
}: {
  open: boolean;
  title: string;
  description?: ReactNode;
  onCancel: () => void;
  children: ReactNode;
  /** 'first' focuses the first control; 'dialog' focuses the dialog itself (safer for destructive prompts). */
  initialFocus?: 'first' | 'dialog';
}) {
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);
  const cancelRef = useRef(onCancel);
  useEffect(() => {
    cancelRef.current = onCancel;
  }, [onCancel]);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = panel.current;
    if (root) {
      const target = initialFocus === 'first' ? focusableWithin(root)[0] : undefined;
      (target ?? root).focus();
    }
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, [open, initialFocus]);

  // Keyboard handling is attached directly to the dialog element (it is not itself a control).
  useEffect(() => {
    const root = panel.current;
    if (!open || !root) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (commandForKey(event) === 'back') {
        event.stopPropagation();
        cancelRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableWithin(root);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) {
        event.preventDefault();
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === root)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    root.addEventListener('keydown', onKeyDown);
    return () => root.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="ui-dialog-backdrop">
      <div
        ref={panel}
        className="ui-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={description ? `${id}-desc` : undefined}
        tabIndex={-1}
      >
        <h2 className="ui-dialog__title" id={`${id}-title`}>
          {title}
        </h2>
        {description && (
          <div className="ui-dialog__description" id={`${id}-desc`}>
            {description}
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
